import { type Dispatch, type SetStateAction, useContext } from 'react'

import BrokerContext from 'components/context/BrokerContext'
import { SplitFilterButton } from 'components/reusable/SplitFilterButton'
import TooltipWrapper from 'components/reusable/TooltipWrapper'
import { Button } from 'slab'
import { generateMonthOptions } from 'utils/dateOptions'
import {
  type BrokersBroker,
  GetBrokersTaskWorkflowsFiltersCoverageTypesItem,
  GetBrokersTaskWorkflowsFiltersEmployerReviewStatusesItem,
  GetBrokersTaskWorkflowsFiltersNextTaskStatusesItem,
  GetBrokersTaskWorkflowsFiltersStatusesItem,
  GetBrokersTaskWorkflowsFiltersWorkflowTaskCategoriesItem,
  GetBrokersTaskWorkflowsFiltersWorkflowTaskExecutionContextsItem,
  GetBrokersTaskWorkflowsFiltersWorkflowTaskExecutionMethodsItem,
  GetBrokersTaskWorkflowsFiltersWorkflowTaskStatusesItem,
  GetBrokersTaskWorkflowsFiltersWorkflowTypesItem,
} from 'utils/web_api.schemas'

import { type WorkflowsFilters } from './workflowsHelpers'

type WorkflowsQuickFiltersProps = {
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
}

const UNASSIGNED_BROKER_ID = 'unassigned'

// Quick filter helper for workflows with the next task = manual
const nextTaskManualAndPending = {
  workflow_task_execution_methods: [
    GetBrokersTaskWorkflowsFiltersWorkflowTaskExecutionMethodsItem.manual,
  ],
  workflow_task_statuses: [
    GetBrokersTaskWorkflowsFiltersNextTaskStatusesItem.pending,
  ],
}

const nextTaskManualForOpsAction = {
  ...nextTaskManualAndPending,
  workflow_task_execution_contexts: [
    GetBrokersTaskWorkflowsFiltersWorkflowTaskExecutionContextsItem.thatch,
  ],
}

// Quick filter for workflows with manual thatch (ops) next tasks
function setUnprocessedEnrollmentsQueue (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>,
  currentBroker: BrokersBroker | undefined
) {
  const brokersBrokerageIdFilter = currentBroker
    ? { brokerage_ids: [currentBroker?.brokerage_id] }
    : {}
  setFilters(() => ({
    workflow_types: [
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.submission,
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.renewal,
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.plan_change,
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.dependent_change,
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.plan_change_and_dependent_change,
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.off_thatch,
    ],
    ...nextTaskManualForOpsAction,
    ...brokersBrokerageIdFilter,
  }))
}

// Quick filter for workflows with manual member next tasks
function setMemberActionRequiredQueue (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>,
  currentBroker: BrokersBroker | undefined
) {
  const brokersBrokerageIdFilter = currentBroker
    ? { brokerage_ids: [currentBroker?.brokerage_id] }
    : {}
  setFilters(() => ({
    workflow_types: [
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.submission,
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.renewal,
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.plan_change,
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.dependent_change,
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.plan_change_and_dependent_change,
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.off_thatch,
    ],
    ...nextTaskManualAndPending,
    workflow_task_execution_contexts: [
      GetBrokersTaskWorkflowsFiltersWorkflowTaskExecutionContextsItem.member,
    ],
    ...brokersBrokerageIdFilter,
  }))
}

function setOffboardingEscalated (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters(() => ({
    workflow_types: [
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.offboarding,
    ],
    ...nextTaskManualForOpsAction,
  }))
}

function setCancellationsQueue (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>,
  currentBroker: BrokersBroker | undefined
) {
  const brokersBrokerageIdFilter = currentBroker
    ? { brokerage_ids: [currentBroker?.brokerage_id] }
    : {}
  setFilters(() => ({
    workflow_types: [
      GetBrokersTaskWorkflowsFiltersWorkflowTypesItem.cancellation,
    ],
    ...nextTaskManualForOpsAction,
    ...brokersBrokerageIdFilter,
  }))
}

// Quick filter for workflows with failed next tasks
function setFailedTasks (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters(() => ({
    workflow_task_statuses: [
      GetBrokersTaskWorkflowsFiltersNextTaskStatusesItem.failed,
    ],
  }))
}

// Quick filter for workflows with manual next tasks
function setManualTasks (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    workflow_task_execution_methods: [
      GetBrokersTaskWorkflowsFiltersWorkflowTaskExecutionMethodsItem.manual,
    ],
  }))
}

// Quick filter for workflows with automated next tasks
function setAutomatedTasks (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    workflow_task_execution_methods: [
      GetBrokersTaskWorkflowsFiltersWorkflowTaskExecutionMethodsItem.automated,
    ],
  }))
}

// Quick filter for workflows with internal next tasks
function setThatchTasks (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    workflow_task_execution_contexts: [
      GetBrokersTaskWorkflowsFiltersWorkflowTaskExecutionContextsItem.thatch,
    ],
  }))
}

// Quick filter for workflows with external next tasks
function setMemberTasks (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    workflow_task_execution_contexts: [
      GetBrokersTaskWorkflowsFiltersWorkflowTaskExecutionContextsItem.member,
    ],
  }))
}

// Quick filter for in-progress workflows
function setInProgressWorkflows (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    statuses: [GetBrokersTaskWorkflowsFiltersStatusesItem.in_progress],
  }))
}

// Quick filter for paused workflows
function setPausedWorkflows (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    statuses: [GetBrokersTaskWorkflowsFiltersStatusesItem.paused],
  }))
}

// Quick filter for pending workflows
function setPendingWorkflows (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    statuses: [GetBrokersTaskWorkflowsFiltersStatusesItem.pending],
  }))
}

// Quick filter for cancelled workflows
function setCanceledWorkflows (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    statuses: [GetBrokersTaskWorkflowsFiltersStatusesItem.canceled],
  }))
}

// Quick filter for completed workflows
function setCompletedWorkflows (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    statuses: [GetBrokersTaskWorkflowsFiltersStatusesItem.completed],
  }))
}

// Quick filter for medical workflows
function setCoverageTypeMedical (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    coverage_types: [GetBrokersTaskWorkflowsFiltersCoverageTypesItem.medical],
  }))
}

// Quick filter for ancillaries workflows
function setCoverageTypeAncillaries (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    coverage_types: [
      GetBrokersTaskWorkflowsFiltersCoverageTypesItem.dental,
      GetBrokersTaskWorkflowsFiltersCoverageTypesItem.vision,
    ],
  }))
}

// Quick filter for dental workflows
function setCoverageTypeDental (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    coverage_types: [GetBrokersTaskWorkflowsFiltersCoverageTypesItem.dental],
  }))
}

// Quick filter for vision workflows
function setCoverageTypeVision (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    coverage_types: [GetBrokersTaskWorkflowsFiltersCoverageTypesItem.vision],
  }))
}

function setEmployerReviewStatusPendingFilters (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    employer_review_statuses: [
      GetBrokersTaskWorkflowsFiltersEmployerReviewStatusesItem.pending,
    ],
  }))
}

function setAssignedToMeFilters (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>,
  currentBroker: BrokersBroker | undefined
) {
  setFilters((prev) => ({
    ...prev,
    ...(currentBroker ? { broker_ids: [currentBroker?.id] } : {}),
  }))
}

function setUnassignedFilters (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>
) {
  setFilters((prev) => ({
    ...prev,
    broker_ids: [UNASSIGNED_BROKER_ID],
  }))
}

// Helper function to set outstanding task filters by category
function setOutstandingTaskFilters (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>,
  category: GetBrokersTaskWorkflowsFiltersWorkflowTaskCategoriesItem
) {
  setFilters(() => ({
    workflow_task_categories: [category],
    workflow_task_statuses: [
      GetBrokersTaskWorkflowsFiltersWorkflowTaskStatusesItem.pending,
      GetBrokersTaskWorkflowsFiltersWorkflowTaskStatusesItem.processing,
      GetBrokersTaskWorkflowsFiltersWorkflowTaskStatusesItem.excluded,
      GetBrokersTaskWorkflowsFiltersWorkflowTaskStatusesItem.blocked,
    ],
  }))
}

// Helper function to set coverage start filters for a specific month
// Both dates are set to the first of the month to filter for that specific start date
function setCoverageStartForMonth (
  setFilters: Dispatch<SetStateAction<WorkflowsFilters>>,
  monthStartDate: string // ISO date string (YYYY-MM-DD) for first day of month
) {
  setFilters((prev) => ({
    ...prev,
    deadline_date_after: monthStartDate,
    deadline_date_before: monthStartDate,
  }))
}

const WorkflowsQuickFilters = ({ setFilters }: WorkflowsQuickFiltersProps) => {
  const { currentBroker } = useContext(BrokerContext)

  // Generate month options for coverage start filters
  const { futureOptions } = generateMonthOptions()
  // Use first value from future months (futureOptions[0]) for primary option labeled as "Next Month"
  const currentMonth = futureOptions[0]
  const nextMonth = futureOptions[1]
  const month2Future = futureOptions[2]
  const month3Future = futureOptions[3]

  return (
    <div className='ml-1 flex items-center justify-start gap-2 flex-shrink-0 flex-wrap'>
      <div className='text-sm font-semibold min-w-16'>Quick filters</div>
      <SplitFilterButton
        variant='primary'
        tooltip='Filter workflows by outstanding tasks in a specific category (submission, payment, or autopay)'
        primaryOption={{
          label: 'Outstanding Tasks: Submission',
          handleSelect: () =>
            setOutstandingTaskFilters(
              setFilters,
              GetBrokersTaskWorkflowsFiltersWorkflowTaskCategoriesItem.submission
            ),
        }}
        additionalOptions={[
          {
            label: 'Payment',
            handleSelect: () =>
              setOutstandingTaskFilters(
                setFilters,
                GetBrokersTaskWorkflowsFiltersWorkflowTaskCategoriesItem.payment
              ),
          },
          {
            label: 'Autopay',
            handleSelect: () =>
              setOutstandingTaskFilters(
                setFilters,
                GetBrokersTaskWorkflowsFiltersWorkflowTaskCategoriesItem.autopay
              ),
          },
        ]}
      />
      <SplitFilterButton
        variant='primary'
        tooltip='Filter enrollments by processing status'
        primaryOption={{
          label: 'Enrollments: Unprocessed',
          handleSelect: () =>
            setUnprocessedEnrollmentsQueue(setFilters, currentBroker),
        }}
        additionalOptions={[
          {
            label: 'Member Action Required',
            handleSelect: () =>
              setMemberActionRequiredQueue(setFilters, currentBroker),
          },
          {
            label: 'Canceled',
            handleSelect: () =>
              setCancellationsQueue(setFilters, currentBroker),
          },
        ]}
      />
      <TooltipWrapper tooltipText='Offboarded enrollments that are overpaid/underpaid and require Ops action to resolve'>
        <Button
          variant='tertiary'
          onClick={() => setOffboardingEscalated(setFilters)}
        >
          Offboarding: Escalated
        </Button>
      </TooltipWrapper>
      <TooltipWrapper tooltipText='Workflows with failed tasks'>
        <Button variant='tertiary' onClick={() => setFailedTasks(setFilters)}>
          Debugging: Failed
        </Button>
      </TooltipWrapper>
      <SplitFilterButton
        variant='tertiary'
        tooltip='Filter workflow tasks by execution method (Manual vs. Automated)'
        primaryOption={{
          label: 'Method: Manual',
          handleSelect: () => setManualTasks(setFilters),
        }}
        additionalOptions={[
          {
            label: 'Automated',
            handleSelect: () => setAutomatedTasks(setFilters),
          },
        ]}
      />
      <SplitFilterButton
        variant='tertiary'
        tooltip='Filter workflow tasks by execution context (Ops vs. Member)'
        primaryOption={{
          label: 'Execution: Ops',
          handleSelect: () => setThatchTasks(setFilters),
        }}
        additionalOptions={[
          {
            label: 'Member',
            handleSelect: () => setMemberTasks(setFilters),
          },
        ]}
      />
      <SplitFilterButton
        variant='tertiary'
        tooltip='Filter workflows by workflow status'
        primaryOption={{
          label: 'Status: In Progress',
          handleSelect: () => setInProgressWorkflows(setFilters),
        }}
        additionalOptions={[
          {
            label: 'Paused',
            handleSelect: () => setPausedWorkflows(setFilters),
          },
          {
            label: 'Pending',
            handleSelect: () => setPendingWorkflows(setFilters),
          },
          {
            label: 'Canceled',
            handleSelect: () => setCanceledWorkflows(setFilters),
          },
          {
            label: 'Completed',
            handleSelect: () => setCompletedWorkflows(setFilters),
          },
        ]}
      />
      <SplitFilterButton
        variant='tertiary'
        tooltip='Filter workflows by coverage type'
        primaryOption={{
          label: 'Coverage: Medical',
          handleSelect: () => setCoverageTypeMedical(setFilters),
        }}
        additionalOptions={[
          {
            label: 'Ancillaries',
            handleSelect: () => setCoverageTypeAncillaries(setFilters),
          },
          {
            label: 'Dental',
            handleSelect: () => setCoverageTypeDental(setFilters),
          },
          {
            label: 'Vision',
            handleSelect: () => setCoverageTypeVision(setFilters),
          },
        ]}
      />
      <TooltipWrapper tooltipText='Filter workflows pending employer review'>
        <Button
          variant='tertiary-deemphasized'
          onClick={() => setEmployerReviewStatusPendingFilters(setFilters)}
        >
          Employer: Pending Review
        </Button>
      </TooltipWrapper>
      <SplitFilterButton
        variant='tertiary'
        tooltip='Filter workflows by assignee'
        primaryOption={{
          label: 'Assignees: Myself',
          handleSelect: () => setAssignedToMeFilters(setFilters, currentBroker),
        }}
        additionalOptions={[
          {
            label: 'Unassigned',
            handleSelect: () => setUnassignedFilters(setFilters),
          },
        ]}
      />
      {nextMonth && (
        <SplitFilterButton
          variant='tertiary'
          tooltip='Filter workflows by deadline date (e.g. coverage start)'
          primaryOption={{
            label: `Deadline: ${nextMonth.label}`,
            handleSelect: () =>
              setCoverageStartForMonth(setFilters, nextMonth.value),
          }}
          additionalOptions={[
            ...(month2Future
              ? [
                  {
                    label: month2Future.label,
                    handleSelect: () =>
                      setCoverageStartForMonth(setFilters, month2Future.value),
                  },
                ]
              : []),
            ...(month3Future
              ? [
                  {
                    label: month3Future.label,
                    handleSelect: () =>
                      setCoverageStartForMonth(setFilters, month3Future.value),
                  },
                ]
              : []),
            ...(currentMonth
              ? [
                  {
                    label: currentMonth.label,
                    handleSelect: () =>
                      setCoverageStartForMonth(setFilters, currentMonth.value),
                  },
                ]
              : []),
          ]}
        />
      )}
    </div>
  )
}

export default WorkflowsQuickFilters
